import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { works } from "@/mocks/works";

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  useDocumentTitle(
    work ? `${work.title} - 杨幂影视资料` : "作品不存在 - 杨幂影视资料",
    work ? `杨幂影视作品《${work.title}》，${work.year}年${work.type}，杨幂饰${work.role}，导演${work.director}。查看剧情简介与相关作品推荐。` : "该作品信息暂缺",
    work ? `${work.title},杨幂${work.type},杨幂影视资料` : "杨幂影视资料",
  );

  if (!work) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">该作品信息暂缺</h1>
        <p className="mt-2 text-sm text-foreground-500">未找到对应的影视作品，可能链接有误。</p>
        <Link
          to="/works"
          className="mt-6 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors"
        >
          返回影视作品库
        </Link>
      </div>
    );
  }

  const related = works
    .filter((w) => w.id !== work.id && (w.type === work.type || Math.abs(w.year - work.year) <= 2))
    .slice(0, 4);

  const meta = [
    { label: "作品类型", value: work.type },
    { label: "上映年份", value: `${work.year} 年` },
    { label: "导演", value: work.director },
    work.type === "电视剧" ? { label: "集数", value: `${work.episodes} 集` } : null,
    { label: "杨幂饰演", value: work.role },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb
        items={[{ label: "首页", path: "/" }, { label: "影视作品", path: "/works" }, { label: work.title }]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
        <div className="lg:col-span-4">
          <div className="w-full max-w-xs mx-auto lg:mx-0 aspect-[2/3] rounded-lg overflow-hidden bg-background-100 border border-background-200">
            <img
              src={work.cover}
              alt={`${work.title}海报`}
              title={`${work.title} - 杨幂影视作品`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Link
              to={`/works?type=${work.type}`}
              className="px-2.5 py-1 rounded-md text-xs font-label bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors"
            >
              {work.type}
            </Link>
            <Link
              to="/works"
              className="px-2.5 py-1 rounded-md text-xs font-label bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
            >
              {work.year} 年
            </Link>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">{work.title}</h1>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {meta.map((m) => (
              <div key={m.label} className="flex items-baseline gap-3 border-b border-background-200 pb-3">
                <span className="text-xs text-foreground-500 font-label w-20 shrink-0">{m.label}</span>
                <span className="text-sm md:text-base text-foreground-900 font-medium">{m.value}</span>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{work.synopsis}</p>
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">主演阵容</h2>
            <div className="flex flex-wrap gap-2">
              {work.cast.map((c) => (
                <span
                  key={c}
                  className={`px-3 py-1.5 rounded-md text-sm font-label ${
                    c === "杨幂" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 border border-background-200"
                  }`}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">作品标签</h2>
            <div className="flex flex-wrap gap-2">
              {work.tags.map((t) => (
                <Link
                  key={t}
                  to={`/works?type=${work.type}`}
                  className="px-3 py-1.5 rounded-md text-sm font-label bg-accent-100 text-accent-900 hover:bg-accent-200 transition-colors"
                >
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关作品推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {related.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}